import React, { useMemo, useRef, useState } from 'react'
import { HStack, VStack } from '@musinsa/mwms-ds'
import { AgGridReact } from 'ag-grid-react'
import { ColDef } from 'ag-grid-community'
import GridTable from '@/components/table/GridTable'
import SummaryPanel from './SummaryPanel'
import {
  ExpectedAllocationItems,
  B2BOrderItem,
  GetB2BOutboundDocumentDetailRes,
  B2BOrderType,
} from '@/services/outbound/outboundWaitingStatus/types'
import { GRID_SIZE } from '@/utils/grid/grid'
import { chunk } from 'lodash-es'
import FilterList from '@/components/content/FilterList'
import { useFormContext } from 'react-hook-form'
import ShipperSelector from '@/components/system/ShipperSelector'
import MultipleInput from '@/components/content/MultipleInput'
import DateRangePickerInput from '@/components/date/DateRangePickerInput'
import dayjs from 'dayjs'
import { DATE_FORMAT } from '@/utils/consts'
import ResizableContents from '@/components/content/ResizableContents'
import styled from 'styled-components'
import { B2BOutboundForm } from './B2BContentWrapper'
import { useGetB2BOutboundDocumentDetail } from '@/services/outbound/outboundWaitingStatus/useOutboundWaitingStatusQuries'
import { flushSync } from 'react-dom'
import { B2BListTableTitle } from './B2BListTableTitle'
import SelectBox from '@/components/content/SelectBox'
import { B2B_ORDER_TYPES } from '@/services/outbound/outboundWaitingStatus/consts'

interface B2BContentProps {
  data: any // API 응답 타입 (ApiPageResponse<OrderItem>)
  expectedAllocationData: ExpectedAllocationItems | undefined
  filteredWarehouses?: string[]
  currentPage: number
  isLoading: boolean
  refetchAllData: () => void
  onSubmit: (data: any) => void
  onChangePage: (page: number) => Promise<void>
  onReset: () => void
  onFieldChange?: (params: { fieldName: any; value: unknown; isSelectedAll?: boolean }) => void
}

const TRANSPORT_TYPE_MAP = {
  PARCEL: '택배',
  HIRED_CAR_DELIVERY: '용차',
}

const B2B_ORDER_TYPE_OPTIONS = Object.entries(B2B_ORDER_TYPES).map(([value, label]) => ({ value: value as B2BOrderType, label }))

function B2BContent({
  data,
  expectedAllocationData,
  filteredWarehouses,
  currentPage,
  isLoading,
  refetchAllData,
  onChangePage,
  onSubmit,
  onReset,
  onFieldChange,
}: B2BContentProps) {
  const $grid = useRef<AgGridReact>(null)

  const methods = useFormContext<B2BOutboundForm>()

  const { setValue, watch, handleSubmit } = methods
  const values = watch()

  const [selectedOutboundDocumentNumber, setSelectedOutboundDocumentNumber] = useState<string | undefined>(undefined)

  const { data: b2bOutboundDocumentDetailData, refetch: refetchB2BOutboundDocumentDetail } = useGetB2BOutboundDocumentDetail({
    outboundDocumentNumber: selectedOutboundDocumentNumber ?? '',
    page: 0,
    size: 50,
  })

  const columnDefs = useMemo<ColDef<B2BOrderItem>[]>(() => {
    return [
      { field: 'outboundDocumentNumber', headerName: '출고문서명', width: 220, pinned: 'left' },
      { field: 'shipperCode', headerName: '화주코드', width: 140 },
      { field: 'shipperName', headerName: '화주명', width: 140 },
      { field: 'plannedCutoffDate', headerName: '출고예정일자', cellDataType: 'dateNday' },
      { field: 'orderType', headerName: '주문유형', width: 140 },
      {
        field: 'transportType',
        headerName: '배송유형',
        valueFormatter: (params) => {
          const transportType = params.data?.transportType

          return TRANSPORT_TYPE_MAP[transportType as keyof typeof TRANSPORT_TYPE_MAP]
        },
      },
      { field: 'orderQuantity', headerName: '출고요청수량' },
      { field: 'createdAt', headerName: '생성일시' },
    ]
  }, [])

  const detailColumnDefs = useMemo<ColDef<GetB2BOutboundDocumentDetailRes>[]>(() => {
    return [
      { field: 'brandName', headerName: '브랜드명', width: 140 },
      { field: 'styleNo', headerName: '스타일넘버', width: 180 },
      { field: 'optionCode', headerName: '옵션코드', width: 120 },
      { field: 'barcode', headerName: '바코드', width: 180 },
      { field: 'skuId', headerName: 'SKUID', width: 150 },
      { field: 'goodsOption', headerName: '옵션', width: 180 },
      { field: 'uid', headerName: 'UID', width: 180 },
      { field: 'goodsName', headerName: '상품명', width: 180 },
      { field: 'orderQuantity', headerName: '출고요청수량', width: 180 },
      { field: 'receiverAddress', headerName: '수령자주소', width: 120, cellDataType: 'maskAddress' },
    ]
  }, [])


  return (
    <VStack gap={16}>
      <FilterList
        isLoading={isLoading}
        list={chunk(
          [
            {
              label: '화주명',
              required: true,
              validationValue: values.shipperNames && values.shipperNames.length > 0,
              value: (
                <ShipperSelector
                  isSingleLogisticsServiceType
                  logisticsServiceTypesWidth={100}
                  shipperNamesWidth={250}
                  onlyFullSearchShipperNamesMode={values.logisticsServiceTypes[0] === 'MFS'}
                  onFieldChange={onFieldChange}
                />
              ),
            },
            {
              label: '주문유형',
              required: true,
              validationValue: true,
              value: (
                <SelectBox<B2BOrderType | ''>
                  block
                  hasAll
                  options={B2B_ORDER_TYPE_OPTIONS}
                  value={values.orderType ?? ''}
                  onChange={(v) => {
                    if (v === '') {
                      setValue('orderType', undefined)
                    } else {
                      setValue('orderType', v as B2BOrderType)
                    }
                  }}
                />
              ),
            },
            {
              label: '출고문서명',
              value: (
                <MultipleInput
                  modalTitle="출고문서명"
                  value={values.outboundDocumentNumbers ?? []}
                  onChange={(v) => setValue('outboundDocumentNumbers', v)}
                  maxSize={200}
                />
              ),
            },
            {
              label: '출고예정일자',
              value: (
                <DateRangePickerInput
                  value={[values.plannedCutoffDateFrom, values.plannedCutoffDateTo]}
                  onChange={([start, end]) => {
                    setValue('plannedCutoffDateFrom', start ? dayjs(start).format(DATE_FORMAT.date) : '')
                    setValue('plannedCutoffDateTo', end ? dayjs(end).format(DATE_FORMAT.date) : '')
                  }}
                />
              ),
            },
          ],
          3,
        )}
        onReset={onReset}
        onSearch={handleSubmit(onSubmit)}
      />
      <HStack full gap={16}>
        <ResizableContentsWrapper
          list={[
            {
              content: (
                <HStack full>
                  <GridTable<B2BOrderItem>
                    title={<B2BListTableTitle $grid={$grid} onRefetch={refetchAllData} />}
                    rowSelectable
                    ref={$grid}
                    isLoading={isLoading}
                    countPerPage={GRID_SIZE.MEDIUM}
                    onChangePage={onChangePage}
                    isRowSelectable={(params) => params.data?.orderType === '사내' && params.data?.orderStatus === 'ORDER_ACCEPTED'}
                    totalCount={data?.pageInfo?.totalCount || 0}
                    totalPages={data?.pageInfo?.totalPages}
                    currentPage={currentPage || 0}
                    rowData={data?.items || []}
                    columnDefs={columnDefs}
                    gridKey="GI23_B2B"
                    legacyStorageKey="b2b-outbound-list"
                    onRowDoubleClicked={async (params) => {
                      flushSync(() => setSelectedOutboundDocumentNumber(params?.data?.outboundDocumentNumber))

                      await refetchB2BOutboundDocumentDetail()
                    }}
                  />
                </HStack>
              ),
            },
            {
              content: (
                <HStack full>
                  <GridTable<GetB2BOutboundDocumentDetailRes>
                    title="상세 목록"
                    useColumnNo
                    isLoading={isLoading}
                    countPerPage={GRID_SIZE.MEDIUM}
                    onChangePage={onChangePage}
                    totalCount={data?.pageInfo?.totalCount || 0}
                    totalPages={data?.pageInfo?.totalPages}
                    currentPage={currentPage || 0}
                    rowData={b2bOutboundDocumentDetailData?.data?.items || []}
                    columnDefs={detailColumnDefs}
                    gridKey="GI23_B2BD"
                    legacyStorageKey="b2b-outbound-detail-list"
                  />
                </HStack>
              ),
            },
          ]}
        />

        <SummaryPanel item={expectedAllocationData} filteredWarehouses={filteredWarehouses} />
      </HStack>
    </VStack>
  )
}

const ResizableContentsWrapper = styled(ResizableContents)`
  width: 100%;
`

export default B2BContent
